/**
 * The words of the User journey page. The sequence and its rules are the
 * back end's (`applications/journey.py`, the chart at the foot of the page); this
 * explains that sequence in plain language. `docs/user-journey.md` is the written
 * form: change the two together.
 */

export type Ball = 'client' | 'system' | 'capx' | 'both';

export const BALL_LABEL: Record<Ball, string> = {
  client: 'With the business',
  system: 'With Flow',
  capx: 'With CapX',
  both: 'Back and forth',
};

export interface Stage {
  key: string;
  title: string;
  ball: Ball;
  summary: string;
  happens: { text: string; rule?: string }[];
  businessSees: string;
  otherwise: { text: string; rule?: string }[];
}

export const STAGES: Stage[] = [
  {
    key: 'enquiry',
    title: 'Enquiry',
    ball: 'client',
    summary:
      'The business, or its agent, sends bank statements. It does not choose a product; CapX does that from the evidence.',
    happens: [
      {
        text: 'Consent to AI assessment is given once, at business setup, and stands for every application.',
        rule: 'FC-DET-051',
      },
      {
        text: 'Statements are uploaded for every account, with one confirmation that nothing is left out. Flow reads the account list off the statements; nobody types it.',
        rule: 'FC-DET-047',
      },
      { text: 'Each document gets a quick AI quality check as it is uploaded.' },
      {
        text: 'Financial statements and other context are encouraged. Nothing waits on them.',
        rule: 'FC-DET-114',
      },
      {
        text: 'The business submits, and the people ticked to hear of new applications get an email.',
      },
    ],
    businessSees: 'A promise of a review within one business day.',
    otherwise: [
      {
        text: 'A trust, an NPO or a business with no CIPC registration goes to a person. This is a referral, not a decline, and the system does not assess it.',
        rule: '§A1.4',
      },
    ],
  },
  {
    key: 'first-reading',
    title: 'First reading',
    ball: 'system',
    summary:
      'Flow reads the statements and works out a provisional size: the pre-assessment. The model extracts, code calculates, and no offer is made on it.',
    happens: [
      { text: 'It runs once, when the business submits.', rule: 'FC-DET-112' },
      {
        text: 'It assumes no other funder has been declared and leaves flagged receipts out of revenue, so the figure may be low. It runs the safe way for CapX.',
        rule: 'FC-DET-048, FC-DET-084',
      },
      {
        text: 'If it gave no figure because the registration date was missing, it runs once more when Ops captures the date.',
        rule: 'FC-DET-115',
      },
      { text: 'Nothing re-runs because a document arrived.', rule: 'FC-DET-112' },
      {
        text: 'Ops sees it in the AI Advisory: where the file could go for each product, and the initial issues. It is an indication and nothing waits on it.',
      },
    ],
    businessSees:
      'No figure today. Publishing one stays switched off until the accuracy re-measurement passes.',
    otherwise: [
      {
        text: 'A funder evidenced in the statements holds up the sizing, and the file refers.',
        rule: 'FC-DET-067',
      },
      { text: 'With no consent or no bank statement it does not run, and Ops is told why.' },
    ],
  },
  {
    key: 'first-look',
    title: "CapX's first look",
    ball: 'capx',
    summary:
      'A person picks the file up, confirms the company and decides where it goes, including whether Debtors Finance is worth offering.',
    happens: [
      {
        text: 'Ops captures the CIPC registration date first. Proceed and Decline wait for it, and trading history runs from it.',
        rule: 'FC-DET-115',
      },
      { text: 'Someone takes the file, so it is clear who is dealing with it.' },
      {
        text: 'A person identifies Debtors Finance where it is an option. This is a judgement, never a gate.',
        rule: 'FC-DET-114, §A2.2',
      },
      {
        text: 'Anything further is asked for by email, and what comes back is filed in the matching block.',
        rule: 'DOC-89',
      },
      {
        text: 'Proceed opens the Kickstart application, the Debtors Finance facility, or both. This step is the build’s, not the rulebook’s.',
      },
    ],
    businessSees: 'An email when CapX proceeds or declines.',
    otherwise: [
      {
        text: 'A decline is never final: the business may update and re-submit.',
        rule: '§A1.3',
      },
      {
        text: 'If CIPC holds no record, Ops records that with a reason and the file goes to a person.',
        rule: '§A1.4',
      },
    ],
  },
  {
    key: 'assessment',
    title: 'Assessment and decision',
    ball: 'capx',
    summary:
      'Ops gathers what the rules read, starts the main assessment when it is ready, and decides or refers.',
    happens: [
      {
        text: 'Ops runs the bureau check by hand, files the report and captures what the rules read. The score declines nothing on its own.',
        rule: 'FC-DET-117, FC-DET-118',
      },
      {
        text: 'Ops asks every business whether it uses another funder or loan, and records the answer.',
        rule: 'FC-DET-048, FC-DET-074',
      },
      {
        text: 'Ops decides each flagged receipt, with a written reason. A decision can change until signature, and the earlier one is kept.',
        rule: 'FC-DET-083, FC-DET-085',
      },
      {
        text: 'The business or its agent gives the tax PIN once the first figure is in; Ops checks it on SARS eFiling.',
        rule: 'FC-DET-107',
      },
      {
        text: 'Ops starts the main assessment. The first press fixes the route: with financials if they are on file, bank-only if not.',
        rule: 'FC-DET-112, FC-DET-114',
      },
    ],
    businessSees: 'The tax PIN request and the outcome. Never a score or the method.',
    otherwise: [
      {
        text: 'Above the ZAR 50,000 floor Ops refers rather than declines. The Credit Committee ends an application if it should end.',
        rule: 'FC-DET-053',
      },
      {
        text: 'Material added or taken out marks the assessment out of date. No offer issues until Ops runs it again.',
        rule: 'FC-DET-112',
      },
    ],
  },
  {
    key: 'offer',
    title: 'Offer and signing',
    ball: 'both',
    summary:
      'Nobody signs for something before they know what it is. The offer comes first; only then is anything asked for signature.',
    happens: [
      {
        text: 'The business sees the amount, fee, total repayable, instalment, term and first debit date. The offer stays open for seven days.',
        rule: 'FC-DET-062, FC-DET-063',
      },
      {
        text: 'Accepting is a non-binding indication. There is no agreement until both parties have signed.',
        rule: 'FC-DET-063',
      },
      { text: 'The business confirms its account list.', rule: 'FC-DET-106' },
      {
        text: 'Ops sends the signing pack once everything due is done. Signing happens outside Flow, and Ops records it.',
        rule: 'FC-DET-110',
      },
      {
        text: 'The business confirms the debit order mandate and the payout account.',
        rule: 'KS-OB-07, KS-OB-08',
      },
    ],
    businessSees: 'The offer in full, and each thing it must do, one at a time.',
    otherwise: [
      {
        text: 'If any term changes, the business sees the new offer and must accept again.',
        rule: 'FC-DET-063',
      },
      {
        text: 'A bank account named after accepting owes statements, and the assessment runs again.',
        rule: 'DOC-86',
      },
    ],
  },
  {
    key: 'payout',
    title: 'Payout and repayment',
    ball: 'capx',
    summary:
      'Ops clears the last conditions and pays out. The business then repays by debit order until Ops confirms the loan is repaid.',
    happens: [
      { text: 'Every onboarding condition is met or waived.', rule: '§A3.2, §A4' },
      { text: 'Ops activates the loan and pays out.', rule: '§B10.1' },
      { text: 'Instalments are collected by debit order over eight months.', rule: '§B11' },
      {
        text: 'Ops confirms the loan repaid at final reconciliation. A settled schedule alone does not do it.',
        rule: '§B11',
      },
    ],
    businessSees: 'An email at payout, then its repayments.',
    otherwise: [
      {
        text: 'A signed loan not paid out within 30 days stops, unless the Credit Committee’s approval is recorded.',
        rule: 'FC-DET-087',
      },
      {
        text: 'Arrears and recovery sit on top of an active loan. There is no “defaulted” state.',
        rule: '§B12',
      },
    ],
  },
];

export interface ProductPath {
  key: string;
  title: string;
  tag: string;
  lead: string;
  points: string[];
  rule: string;
}

export const PRODUCT_PATHS: ProductPath[] = [
  {
    key: 'ks',
    title: 'Kickstart Cash',
    tag: 'Every application',
    lead: 'Every new application is assessed for Kickstart. The business does not ask for it.',
    points: [
      'Bank statements are the base: 6 months on the principal account with financial statements, 12 months without.',
      'The route is fixed when Ops first starts the main assessment, and then locked.',
      'Financial statements arriving after that do not change the route; a new application does.',
    ],
    rule: 'FC-DET-114, §A3.2, §B3',
  },
  {
    key: 'df',
    title: 'Debtors Finance',
    tag: 'Where a person identifies it',
    lead: 'A person at CapX offers it where the application shows it as an option. The system does not assess it yet.',
    points: [
      'A business relying on bank statements alone is not eligible. The path opens once financial statements are in.',
      'It needs an age analysis, an approved debtor and a cession or administered-account arrangement.',
      'Conditions first, then a human credit decision with active insurance. Above ZAR 1,000,000 the Credit Committee signs off.',
    ],
    rule: 'FC-DET-114, §A2.4, §A3.3, §C1.2',
  },
  {
    key: 'both',
    title: 'Both',
    tag: 'Side by side',
    lead: 'The products are non-exclusive, and a business may hold both.',
    points: [
      'Both may run in parallel. Neither blocks, cancels or suspends the other.',
      'Being assessed for Kickstart is not a decline for Debtors Finance.',
      'The same documents carry across, so the business is not asked twice.',
    ],
    rule: '§A2.1, §A2.2',
  },
];

export type Channel = 'flow' | 'email' | 'outside';

export const CHANNEL_LABEL: Record<Channel, string> = {
  flow: 'In Flow',
  email: 'By email',
  outside: 'Outside Flow',
};

export interface Exchange {
  from: 'business' | 'capx';
  channel: Channel;
  title: string;
  text: string;
  rule?: string;
}

export const EXCHANGES: Exchange[] = [
  {
    from: 'business',
    channel: 'flow',
    title: 'Sends the application',
    text: 'Bank statements, the completeness confirmation, and anything else it wants CapX to read.',
    rule: 'FC-DET-114',
  },
  {
    from: 'capx',
    channel: 'email',
    title: 'Asks for anything further',
    text: 'Flow itself asks for nothing after submission: Ops writes to the business directly.',
    rule: 'DOC-89',
  },
  {
    from: 'business',
    channel: 'email',
    title: 'Replies with documents',
    text: 'Ops files them in the matching block. The business may also add to its blocks in Flow.',
    rule: 'DOC-89',
  },
  {
    from: 'capx',
    channel: 'outside',
    title: 'Asks about other funders',
    text: 'Every business is asked, after the pre-assessment. Ops records the answer in Flow.',
    rule: 'FC-DET-048',
  },
  {
    from: 'business',
    channel: 'flow',
    title: 'Gives the tax PIN',
    text: 'One of the few things Flow does ask for, because the rules make it the business’s to give.',
    rule: 'FC-DET-107',
  },
  {
    from: 'capx',
    channel: 'flow',
    title: 'Issues the offer',
    text: 'Ops runs the assessment again first if anything was filed since the last run.',
    rule: 'FC-DET-062, FC-DET-112',
  },
  {
    from: 'business',
    channel: 'flow',
    title: 'Accepts and confirms its accounts',
    text: 'If a term changes, the offer comes back and is accepted again.',
    rule: 'FC-DET-063, FC-DET-106',
  },
  {
    from: 'capx',
    channel: 'outside',
    title: 'Sends the signing pack',
    text: 'Only when everything due before signing is done. Ops records that it went.',
    rule: 'FC-DET-110',
  },
  {
    from: 'business',
    channel: 'outside',
    title: 'Signs and returns it',
    text: 'Ops records both signatures, then the business confirms its mandate and payout account in Flow.',
    rule: 'FC-DET-110, KS-OB-06/07/08',
  },
];

export interface DocumentRow {
  name: string;
  who: string;
  when: string;
  channel: Channel;
  unlocks: string;
  rule: string;
}

export const DOCUMENTS: DocumentRow[] = [
  {
    name: 'Bank statements, every account',
    who: 'Business or agent',
    when: 'Before submitting',
    channel: 'flow',
    unlocks: 'The pre-assessment and the account list',
    rule: 'FC-DET-047',
  },
  {
    name: 'Financial statements',
    who: 'Business or agent',
    when: 'Encouraged any time; matters most before the main assessment',
    channel: 'flow',
    unlocks: 'The with-financials route, and the path into Debtors Finance',
    rule: 'FC-DET-114, §A2.4',
  },
  {
    name: 'Anything further Ops asks for',
    who: 'Business, on request',
    when: 'Between submission and the main assessment',
    channel: 'email',
    unlocks: 'Read by the next assessment, once Ops files it and runs it',
    rule: 'DOC-89, FC-DET-112',
  },
  {
    name: 'CIPC document',
    who: 'Ops obtains it',
    when: "Ops's first act on the file",
    channel: 'outside',
    unlocks: 'Trading history; Proceed and Decline',
    rule: 'FC-DET-115',
  },
  {
    name: 'Bureau report',
    who: 'Ops obtains it',
    when: 'Before the main assessment',
    channel: 'outside',
    unlocks: 'The main assessment and any offer',
    rule: 'FC-DET-117',
  },
  {
    name: 'Tax compliance PIN',
    who: 'Business or agent',
    when: 'Once the first figure is in',
    channel: 'flow',
    unlocks: 'The SARS check Ops records',
    rule: 'FC-DET-107',
  },
  {
    name: 'Signed loan agreement and suretyship',
    who: 'Business, sureties and CapX',
    when: 'After the offer is accepted and the pack is sent',
    channel: 'outside',
    unlocks: 'Payout',
    rule: 'FC-DET-110, KS-OB-06',
  },
  {
    name: 'Age analysis',
    who: 'Business or agent',
    when: 'Debtors Finance only, with its application',
    channel: 'flow',
    unlocks: 'The Debtors Finance conditions',
    rule: 'DF-OB-04',
  },
];

export interface Person {
  name: string;
  lane: Exclude<Ball, 'both'> | 'none';
  wants: string;
  mayNot: string;
}

export const PEOPLE: Person[] = [
  {
    name: 'The business',
    lane: 'client',
    wants: 'To know whether it can get funding, and what to do next.',
    mayNot: 'Never sees a score, the method or the AI’s wording.',
  },
  {
    name: 'The introducing agent',
    lane: 'client',
    wants: 'To apply for a client without chasing, and to see where each file stands.',
    mayNot: 'Cannot give a director’s bureau consent, or remove a name from the directors list.',
  },
  {
    name: 'A director, shareholder or surety',
    lane: 'client',
    wants: 'To consent and sign for themselves, and nothing else.',
    mayNot: 'Does not run the application.',
  },
  {
    name: 'Flow',
    lane: 'system',
    wants: 'Reads documents, calculates by fixed rules, and shows its working to Ops.',
    mayNot: 'The model never does the arithmetic, and nothing it writes decides a file.',
  },
  {
    name: 'Authorised Ops user',
    lane: 'capx',
    wants: 'To see what is on the file, what is missing and what to do next.',
    mayNot: 'Cannot go above their authority, or decline above the floor.',
  },
  {
    name: 'Credit Committee',
    lane: 'capx',
    wants: 'To decide what is referred up, with the reasons in front of them.',
    mayNot: 'Acts only by a recorded resolution.',
  },
  {
    name: 'Product Owner',
    lane: 'none',
    wants: 'Sets the rules the whole map follows.',
    mayNot: 'Is not a step on any file.',
  },
];

export const NOT_SETTLED: string[] = [
  'The rulebook has no section on the pre-assessment. It is defined in a note, and its Ops output and Debtors Finance reading are the build’s own.',
  'Proceed, and the separate Kickstart application that follows it, are the build’s. The rules treat the submitted enquiry as the application.',
  'The one-business-day promise is the build’s wording, not a rule.',
  'The Debtors Finance assessment is parked, and what counts as “identifying” it is left to a person.',
  'The Advisory’s “worth asking about” for Debtors Finance is the build’s working test (three or more named payers, an age analysis, or the client asking). It prompts a person and gates nothing.',
];
